import { useEffect, useState, useCallback } from "react";
import api, { fmtRM, fmtDate, downloadFile } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Flame, Phone, FileSpreadsheet } from "lucide-react";

export const LEAD_STAGES = ["new_lead", "contacted", "qualified", "quotation", "follow_up", "booking", "deposit", "full_payment", "travelled"];

const STAGE_STYLE = {
  new_lead: "border-blue-300 bg-blue-50 text-blue-700",
  contacted: "border-indigo-300 bg-indigo-50 text-indigo-700",
  qualified: "border-purple-300 bg-purple-50 text-purple-700",
  quotation: "border-amber-300 bg-amber-50 text-amber-700",
  follow_up: "border-orange-300 bg-orange-50 text-orange-700",
  booking: "border-teal-300 bg-teal-50 text-teal-700",
  deposit: "border-emerald-300 bg-emerald-50 text-emerald-700",
  full_payment: "border-green-300 bg-green-100 text-green-800",
  travelled: "border-sky-300 bg-sky-50 text-sky-700",
  lost: "border-red-300 bg-red-50 text-red-700",
  cancelled: "border-slate-300 bg-slate-100 text-slate-600",
};

export const StatusBadge = ({ status }) => (
  <span data-testid={`status-badge-${status}`}
    className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-mono uppercase tracking-wide ${STAGE_STYLE[status] || STAGE_STYLE.cancelled}`}>
    {status.replace(/_/g, " ")}
  </span>
);

const SOURCES = ["facebook_ads", "instagram", "tiktok", "google", "website", "whatsapp", "walk_in", "referral", "agent", "matta_fair", "corporate"];

export default function Leads() {
  const { t } = useLang();
  const [leads, setLeads] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", source: "website", package_interest: "", pax: 2, budget: 8000 });

  const load = useCallback(() => {
    api.get("/leads").then((r) => setLeads(r.data)).catch(() => toast.error("Failed to load leads"));
  }, []);
  useEffect(load, [load]);

  const createLead = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leads", { ...form, pax: Number(form.pax), budget: Number(form.budget) });
      toast.success("Lead added");
      setOpen(false);
      setForm({ name: "", phone: "", source: "website", package_interest: "", pax: 2, budget: 8000 });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add lead");
    }
  };

  const moveStage = async (lead, status) => {
    try {
      await api.patch(`/leads/${lead.lead_id}`, { status });
      setLeads((ls) => ls.map((l) => (l.lead_id === lead.lead_id ? { ...l, status } : l)));
    } catch {
      toast.error("Failed to update");
    }
  };

  if (!leads) return <div className="flex justify-center h-64 items-center" data-testid="leads-loading"><div className="w-8 h-8 border-4 border-brand-800 border-t-gold-500 rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-5" data-testid="leads-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-sans text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">{t("app.leads")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{leads.length} leads in pipeline</p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="export-leads-csv" variant="outline" onClick={() => downloadFile("/reports/leads.csv", "leads.csv")}
            className="rounded-full border-sand-border hover:border-emerald-700/40 hover:text-emerald-800">
            <FileSpreadsheet className="w-4 h-4 mr-1.5" />Export
          </Button>
          <Button data-testid="add-lead-button" onClick={() => setOpen(true)} className="bg-brand-800 hover:bg-brand-700 text-sand rounded-full">
            <Plus className="w-4 h-4 mr-1.5" />{t("common.add")} Lead
          </Button>
        </div>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-4 -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="pipeline-kanban">
        {LEAD_STAGES.map((stage) => {
          const stageLeads = leads.filter((l) => l.status === stage);
          return (
            <div key={stage} className="min-w-[240px] w-60 shrink-0" data-testid={`kanban-column-${stage}`}>
              <div className={`px-3 py-2 rounded-t-xl border border-b-0 ${STAGE_STYLE[stage]}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-mono uppercase tracking-wider">{stage.replace(/_/g, " ")}</span>
                  <span className="text-xs font-semibold tabular-nums">{stageLeads.length}</span>
                </div>
                <div className="text-[9px] font-mono opacity-70 mt-0.5 tabular-nums">{fmtRM(stageLeads.reduce((s, l) => s + (l.budget || 0), 0))} pipeline</div>
              </div>
              <div className="bg-white/60 border border-sand-border rounded-b-xl p-2 space-y-2 min-h-[200px] max-h-[60vh] overflow-y-auto">
                {stageLeads.map((lead) => (
                  <div key={lead.lead_id} data-testid={`crm-lead-card-${lead.lead_id}`}
                    className="bg-white rounded-lg border border-sand-border p-3 shadow-sm hover:shadow-md hover:border-emerald-700/25 transition-all">
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm font-medium text-brand-950 leading-tight">{lead.name}</div>
                      {lead.lead_score >= 75 && <Flame className="w-3.5 h-3.5 text-gold-600 shrink-0" />}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                      <Phone className="w-3 h-3" />{lead.phone}
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-[10px] font-mono text-muted-foreground uppercase">{lead.source.replace(/_/g, " ")}</span>
                      <span className="text-[10px] font-mono text-gold-700">{lead.pax} pax</span>
                    </div>
                    {lead.budget && <div className="text-xs font-mono text-brand-800 mt-1.5">{fmtRM(lead.budget)}</div>}
                    <Select value={lead.status} onValueChange={(v) => moveStage(lead, v)}>
                      <SelectTrigger data-testid={`lead-stage-select-${lead.lead_id}`} className="h-7 mt-2 text-[10px] font-mono uppercase">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[...LEAD_STAGES, "lost", "cancelled", "postponed", "not_ready"].map((s) => (
                          <SelectItem key={s} value={s} className="text-xs">{s.replace(/_/g, " ")}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
                {stageLeads.length === 0 && <div className="text-center text-xs text-muted-foreground/50 py-8">—</div>}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="add-lead-dialog" className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-sans font-semibold text-lg">{t("common.add")} Lead</DialogTitle></DialogHeader>
          <form onSubmit={createLead} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>{t("common.name")}</Label>
                <Input data-testid="lead-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <Label>{t("common.phone")}</Label>
                <Input data-testid="lead-phone-input" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+6012…" />
              </div>
              <div>
                <Label>Source</Label>
                <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                  <SelectTrigger data-testid="lead-source-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SOURCES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Package Interest</Label>
                <Input data-testid="lead-package-input" value={form.package_interest} onChange={(e) => setForm({ ...form, package_interest: e.target.value })} placeholder="Umrah Premium…" />
              </div>
              <div>
                <Label>Pax</Label>
                <Input data-testid="lead-pax-input" type="number" min="1" value={form.pax} onChange={(e) => setForm({ ...form, pax: e.target.value })} />
              </div>
              <div>
                <Label>Budget (RM)</Label>
                <Input data-testid="lead-budget-input" type="number" min="0" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} />
              </div>
            </div>
            <Button data-testid="lead-submit-button" type="submit" className="w-full bg-brand-800 hover:bg-brand-700 text-sand rounded-full">
              {t("common.save")}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
